import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react';
import { API } from 'aws-amplify';
import { useApiError } from './ApiErrorProvider';
import { useAuth } from './Auth';
import { error } from '../constants/bindings';

interface FeatureItem {
  moduleId: number;
  user: string;
  moduleName: string;
  isEnabled: boolean;
  isDisplay: boolean; // manually controlled
}

type FeatureState = FeatureItem[];

interface AdminAppControlContextType {
  featureState: FeatureState;
  setFeatureState: (moduleId: number, value: boolean) => Promise<void>;
  refreshFeatureState: () => Promise<void>;
  isLoading: boolean;
  getFeatureByModuleId: (moduleId: number) => FeatureItem | undefined;
  getFeatureByName: (moduleName: string) => FeatureItem | undefined;
}

const AdminAppControlContext = createContext<
  AdminAppControlContextType | undefined
>(undefined);

export const useAdminAppControl = () => {
  const context = useContext(AdminAppControlContext);
  if (!context) {
    throw new Error(
      'useAdminAppControl must be used within an AdminAppControlProvider'
    );
  }
  return context;
};

interface AdminAppControlProviderProps {
  children: ReactNode;
}

export const AdminAppControlProvider: React.FC<
  AdminAppControlProviderProps
> = ({ children }) => {
  const [featureState, setFeatureStateInternal] = useState<FeatureState>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setErrorCode } = useApiError();
  const { user } = useAuth(); // Add authentication check

  const setFeatureState = async (moduleId: number, value: boolean) => {
    try {
      setIsLoading(true);
      // Find the feature item to get other properties
      const existingFeature = featureState.find(
        (item) => item.moduleId === moduleId
      );

      if (!existingFeature) {
        console.warn(`Feature with moduleId ${moduleId} not found`);
        return;
      }

      await API.post('crewlife', `/moduleSwitch/put`, {
        body: {
          isEnabled: value,
          user: existingFeature.user,
          moduleName: existingFeature.moduleName,
          moduleId: moduleId
        }
      });

      // Update the state by finding and updating the specific item
      setFeatureStateInternal((prev) =>
        prev.map((item) =>
          item.moduleId === moduleId ? { ...item, isEnabled: value } : item
        )
      );
      setIsLoading(false);
    } catch (err: any) {
      if (err.response?.status) {
        setErrorCode(err.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      setIsLoading(false);
    }
  };

  // Helper function to get feature by moduleId
  const getFeatureByModuleId = (moduleId: number): FeatureItem | undefined => {
    return featureState.find((item) => item.moduleId === moduleId);
  };

  // Helper function to get feature by name
  const getFeatureByName = (moduleName: string): FeatureItem | undefined => {
    return featureState.find((item) => item.moduleName === moduleName);
  };

  const refreshFeatureState = async () => {
    // Only fetch if user is authenticated
    if (!user) {
      console.warn('Cannot refresh feature state: User not authenticated');
      return;
    }

    try {
      setIsLoading(true);
      const data = await API.get('crewlife', '/moduleSwitch/get', {});
      // Ensure data is an array of FeatureItem objects
      if (Array.isArray(data.modules)) {
        const result = data.modules.map((item: any) => ({
          moduleId: item.moduleId,
          user: item.user,
          moduleName: item.moduleName,
          isEnabled: item.isEnabled !== 0 ? true : false,
          isDisplay: [9, 25, 26].includes(item.moduleId) // Display SDO Request (9), SIM Request (25), and ART Request (26)
        }));
        setFeatureStateInternal(result);
      } else {
        setFeatureStateInternal([]);
      }
    } catch (err: any) {
      if (err.response?.status) {
        setErrorCode(err.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      setFeatureStateInternal([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch feature state when user is authenticated
    if (!user) {
      // Clear feature state when user is not authenticated
      setFeatureStateInternal([]);
      setIsLoading(false);
      return;
    }

    const fetchFeatureState = async () => {
      try {
        setIsLoading(true);
        const data = await API.get('crewlife', '/moduleSwitch/get', {});
        // Ensure data is an array of FeatureItem objects
        if (Array.isArray(data.modules)) {
          const result = data.modules.map((item: any) => ({
            moduleId: item.moduleId,
            user: item.user,
            moduleName: item.moduleName,
            isEnabled: item.isEnabled !== 0 ? true : false,
            isDisplay: [9, 25, 26].includes(item.moduleId) // Display SDO Request (9), SIM Request (25), and ART Request (26)
          }));
          setFeatureStateInternal(result);
        } else {
          setFeatureStateInternal([]);
        }
      } catch (err: any) {
        if (err.response?.status) {
          setErrorCode(err.response?.status);
        } else {
          setErrorCode(error.DEFAULT);
        }
        setFeatureStateInternal([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatureState();
  }, [user, setErrorCode]); // Include user in dependencies

  return (
    <AdminAppControlContext.Provider
      value={{
        featureState,
        setFeatureState,
        refreshFeatureState,
        isLoading,
        getFeatureByModuleId,
        getFeatureByName
      }}
    >
      {children}
    </AdminAppControlContext.Provider>
  );
};
